import React from "react";
import {
  TouchableOpacity,
  ActivityIndicator,
  View,
  type TouchableOpacityProps,
} from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { cva } from "@louise/utils/cva";
import { twMerge } from "tailwind-merge";
import { pressLabel, usePressCapture } from "@louise/analytics/usePressCapture";

type ButtonVariant = "outlined" | "primary";

type ButtonProps = Omit<TouchableOpacityProps, "disabled"> & {
  variant?: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
};

const buttonClasses = cva({
  base: "rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed",
  variants: {
    variant: {
      primary: "bg-louise-primary-500",
      outlined: "border border-gray-300",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

const textClasses = cva({
  base: "text-center",
  variants: {
    variant: {
      primary: "text-white",
      outlined: "text-gray-700",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export function Button({
  variant = "primary",
  loading = false,
  disabled = false,
  children,
  className,
  onPress,
  ...props
}: ButtonProps) {
  const buttonClassName = buttonClasses({
    variant,
  });

  const textClassName = textClasses({ variant });
  const capturePress = usePressCapture("Button");

  return (
    <TouchableOpacity
      disabled={disabled || loading}
      className={twMerge(buttonClassName, className)}
      {...props}
      onPress={(event) => {
        capturePress(
          pressLabel(
            typeof children === "string" ? children : undefined,
            props.accessibilityLabel,
            props.testID,
          ),
        );
        onPress?.(event);
      }}
    >
      <View className="inline-flex flex-row items-center justify-center gap-2">
        <Text className={textClassName}>{children}</Text>
        {loading && (
          <ActivityIndicator
            color={variant === "primary" ? "#fff" : "#374151"}
            className="size-4"
          />
        )}
      </View>
    </TouchableOpacity>
  );
}
